import React from 'react'

/**
 * Progress panel for an Analytics export.
 *
 * Exports are built by the form-data-normalization worker and delivered by
 * email — a full one runs to ~90 MB, so there is deliberately no download
 * here. The panel reports one job through three states:
 *
 *   generating          the worker has not finished building the file
 *   file generated,     the file exists but the mail has not gone out yet;
 *   sending to email    a visible gap on a large export, so it gets its own
 *                       wording rather than looking stuck on "generating"
 *   sent                notified — the requester can go to their inbox
 *
 * Shared by Candidate Metric Details (live candidates) and Candidate Metrics
 * (ingested candidates), which run the same job pipeline.
 *
 * @param {object|null} status  { status, requestedAt, notified, notifyEmail, errorMessage }
 * @param {() => void} onDismiss clears the panel
 */
const ExportStatusPanel = ({ status, onDismiss }) => {
  if (!status) return null

  const formatRequestedAt = iso => {
    if (!iso) return ''
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    })
  }

  return (
          <div style={{
            border: '0.5px solid #EF9F27',
            borderRadius: 10,
            background: '#FAEEDA',
            padding: '12px 16px',
            marginBottom: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            width: '100%',
            marginTop: 8,
          }}>
            {/* Outer amber row — always visible */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: '#BA7517', flexShrink: 0,
                animation: status.status === 'pending' || status.status === 'processing'
                  ? 'exportPulse 1.5s ease-in-out infinite' : 'none',
              }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: '#633806' }}>
                Export
              </span>
              <span style={{ fontSize: 12, color: '#854F0B' }}>
                · Requested on {formatRequestedAt(status.requestedAt)}
              </span>
              {!status.notified && status.status !== 'failed' &&
                status.status !== 'expired' && (
                <span style={{ fontSize: 12, color: '#854F0B', marginLeft: 'auto' }}>
                  {/* 'done' means the file exists but the mail has not gone yet
                      — a real, visible gap on a 90 MB export, so it gets its
                      own wording rather than looking stuck on "generating". */}
                  {status.status === 'done'
                    ? 'File generated — sending it to your email...'
                    : 'Generating your file — you can close this page, we\'ll email it when it\'s ready.'}
                </span>
              )}
            </div>

            {/* Inner green row — the file has been emailed. There is no
                download button: a full export is ~90 MB, which is why delivery
                moved to email in the first place. */}
            {status.notified && (
              <div style={{
                border: '0.5px solid #97C459',
                borderRadius: 8,
                background: '#EAF3DE',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: '#3B6D11', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ color: '#EAF3DE', fontSize: 10, lineHeight: 1 }}>✓</span>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#27500A' }}>
                    File generated and sent to your email
                  </span>
                  <span style={{ fontSize: 12, color: '#3B6D11', marginLeft: 8 }}>
                    {status.notifyEmail
                      ? `Check ${status.notifyEmail} for the download link.`
                      : 'Check your inbox for the download link.'}
                  </span>
                </div>
                <span
                  style={{ fontSize: 12, color: '#3B6D11', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                  onClick={() => onDismiss()}
                >Dismiss</span>
              </div>
            )}

            {/* Inner grey row — the retention sweep has released the file */}
            {status.status === 'expired' && (
              <div style={{
                border: '0.5px solid #D5D9E0',
                borderRadius: 8,
                background: '#F4F6FA',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#242E39' }}>Export expired</span>
                  <span style={{ fontSize: 12, color: '#637487', marginLeft: 8 }}>
                    This file is no longer available. Please run the export again.
                  </span>
                </div>
                <span
                  style={{ fontSize: 12, color: '#637487', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                  onClick={() => onDismiss()}
                >Dismiss</span>
              </div>
            )}

            {/* Inner red row — only when failed */}
            {status.status === 'failed' && (
              <div style={{
                border: '0.5px solid #F09595',
                borderRadius: 8,
                background: '#FCEBEB',
                padding: '10px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <div style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: '#A32D2D', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ color: '#FCEBEB', fontSize: 10, lineHeight: 1 }}>✕</span>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#791F1F' }}>Export failed</span>
                  <span style={{ fontSize: 12, color: '#A32D2D', marginLeft: 8 }}>
                    {status.errorMessage || 'Something went wrong. Please try again.'}
                  </span>
                </div>
                <span
                  style={{ fontSize: 12, color: '#A32D2D', cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                  onClick={() => onDismiss()}
                >Dismiss</span>
              </div>
            )}
          </div>
  )
}

export default ExportStatusPanel
